import { getAnalytics } from "firebase/analytics"
import { getApp, getApps, initializeApp } from "firebase/app"
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check"
import { connectAuthEmulator, getAuth } from "firebase/auth"
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
} from "firebase/firestore"
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
} from "firebase/functions"
import { fetchAndActivate, getRemoteConfig } from "firebase/remote-config"
import { connectStorageEmulator, getStorage } from "firebase/storage"

import {
  devOrigin,
  useDevAuth,
  useDevBackend,
  useForceLongPolling,
} from "@settingsAlias/settings"

import { isDev } from "@fuse/utils/common"

// ✅ Firebase config from .env
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DB_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MSG_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_GA_MEASUREMENTID,
}

// ✅ Initialize only once
const app = getApps().length ? getApp() : initializeApp(firebaseConfig)

const firestore = useForceLongPolling
  ? initializeFirestore(app, { experimentalForceLongPolling: true })
  : getFirestore(app)
const storage = getStorage(app)
const firebaseFunctions = getFunctions(app)
const auth = getAuth(app)
const remoteConfig = getRemoteConfig(app)
const analytics = getAnalytics(app)

// ✅ Optional: Listen for auth state
auth.onAuthStateChanged((user) => {
  if (user) {
    auth.currentUser?.reload()
    console.log("User is signed in")
  } else {
    console.log("User is signed out")
  }
})

// ✅ Emulator setup for development
if (useDevBackend) {
  const origin =
    devOrigin || window.location.hostname?.split(":").shift() || "localhost"

  connectFunctionsEmulator(firebaseFunctions, origin, 5001)
  connectFirestoreEmulator(firestore, origin, 8080)
  connectStorageEmulator(storage, origin, 9199)

  if (useDevAuth) {
    connectAuthEmulator(auth, `http://${origin}:9099`)
  }
}
;(self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = isDev()
// ✅ Allow App Check debug mode (for dev)
let appCheck: ReturnType<typeof initializeAppCheck> | undefined

if (!isDev()) {
  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(import.meta.env.VITE_FIREBASE_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  })
}

// 🔴 Do not initialize AppCheck in dev with ReCaptchaV3Provider — causes phone auth to fail!

export const firebaseStorage = storage
export const firebaseApp = app
const currentUser = auth.currentUser
const functionsAccessor = {
  httpsCallable: (functionName: string) =>
    httpsCallable(firebaseFunctions, functionName),
}

// ✅ Setup Remote Config
export const initializeRemoteConfig = async (): Promise<void> => {
  remoteConfig.settings.minimumFetchIntervalMillis = 3600000 // 1 hour
  remoteConfig.defaultConfig = {
    example_key: "default_value",
    ticketed_events_web: false,
  }

  try {
    await fetchAndActivate(remoteConfig)
    console.log("Remote config values retrieved and activated")
  } catch (error) {
    console.error("Error fetching remote config values:", error)
  }
}

initializeRemoteConfig()

export {
  app,
  auth,
  currentUser,
  firestore,
  storage,
  firebaseFunctions,
  functionsAccessor,
  remoteConfig,
  analytics,
  appCheck,
}
